import styled from 'styled-components';
import { Message, Modal, Segment, Table } from 'semantic-ui-react';

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const PageActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

export const CreateModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  i.icon {
    color: ${props => props.theme.main.color};
    margin: 0;
  }
`;

export const CreateModalContent = styled(Modal.Content)`
  &&& {
    overflow: visible !important;
  }

  .ui.dropdown > .menu {
    z-index: 100;
  }
`;

export const CreateSectionTitle = styled.h3`
  margin: 1.75rem 0 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #ddc7f2;
  color: #59249b;
  font-family: Open Sans, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 100%;
  letter-spacing: 0;

  &:first-child {
    margin-top: 0;
  }
`;

export const PageHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.3;
`;

export const PageSubtitle = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: #6b7280;
  line-height: 1.5;
  max-width: 640px;
`;

export const ContentSegment = styled(Segment)`
  &&& {
    border: 1px solid #e8e4ef;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(89, 36, 155, 0.06);
    padding: 1.25rem 1.5rem 1.5rem;
  }
`;

export const TableSectionHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 16px;
`;

export const TableSectionTitle = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #374151;
`;

export const ResultsBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 500;
  color: ${props => props.theme.main.color};
  background: rgba(89, 36, 155, 0.08);
  border: 1px solid rgba(89, 36, 155, 0.12);
`;

export const TableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid #ebe6f2;
  border-radius: 8px;

  .ui.table {
    margin: 0;
    border: none;
    border-radius: 8px;
  }

  .ui.table thead th {
    background: #f7f4fb !important;
    color: #4b5563;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    border-bottom: 1px solid #ebe6f2 !important;
  }

  .ui.table tbody tr:hover {
    background: #faf8fd !important;
  }
`;

export const StyledTable = styled(Table)`
  &&& {
    font-size: 0.92rem;
  }
`;

export const ActivationMessage = styled(Message)`
  &&& {
    border-radius: 8px;
    border: 1px solid rgba(89, 36, 155, 0.2);
    background: linear-gradient(135deg, #faf8fd 0%, #f3effa 100%);
  }
`;

export const ActivationText = styled.p`
  margin: 0 0 12px;
  color: #374151;
  line-height: 1.5;
`;

export const ActivationError = styled.p`
  margin: 12px 0 0;
  color: #9f3a38;
  font-size: 0.9rem;
`;

export const EmptyStateWrapper = styled.div`
  padding: 2rem 1rem;
  text-align: center;
`;

export const LoadingWrapper = styled.div`
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const DetailsModalSubtitle = styled.p`
  margin: 6px 0 0;
  font-size: 0.9rem;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.45;
`;

export const CondominiumModalHeader = styled(Modal.Header)`
  &&& {
    padding: 0.9rem 1.25rem !important;
    background: #f4f4f5 !important;
    color: #303038;
    font-size: 0.95rem;
    font-weight: 600;
  }

  i.icon {
    margin-right: 0.5rem;
    color: ${props => props.theme.main.color};
  }
`;

export const DetailsModalActions = styled(Modal.Actions)`
  &&& {
    padding: 0.75rem 1.25rem;
    border-top: 1px solid #e5e7eb;
    background: #f7f7f8;
  }
`;

export const DetailsTabs = styled.div`
  &&& {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 0 0 2rem;
    padding: 0;
  }
`;

export const DetailsTabButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  flex: 0 0 140px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 140px;
  height: 42px;
  box-sizing: border-box;
  border: 0;
  border-radius: 4px;
  padding: 8px 12px;
  color: ${props => (props.$active ? '#ffffff' : '#596273')};
  background: ${props => (props.$active ? '#854EE0' : '#ffffff')};
  font: inherit;
  font-size: 14px;
  font-weight: ${props => (props.$active ? 600 : 400)};
  line-height: 1;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;

  &:hover {
    color: ${props => (props.$active ? '#ffffff' : '#59249b')};
    background: ${props => (props.$active ? '#854EE0' : '#f5f0fa')};
  }

  &:focus-visible {
    outline: 2px solid #59249b;
    outline-offset: 2px;
  }
`;

export const DetailsSectionTitle = styled.h3`
  margin: 0 0 0.85rem;
  padding-bottom: 0.45rem;
  border-bottom: 1px solid #ddc7f2;
  color: #59249b;
  font-family: Open Sans, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 100%;
  letter-spacing: 0;
  text-transform: none;
`;

export const DetailsSection = styled.section`
  & + & {
    margin-top: 1.5rem;
  }
`;

export const DetailFieldGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.$columns || 2}, minmax(0, 1fr));
  gap: 12px 16px;

  @media (max-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const DetailField = styled.div`
  min-width: 0;
`;

export const DetailFieldLabel = styled.span`
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #9ca3af;
  margin-bottom: 4px;
`;

export const DetailFieldValue = styled.span`
  display: block;
  font-size: 0.92rem;
  color: #374151;
  line-height: 1.4;
  word-break: break-word;
`;

export const ProcessBadgesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

export const ProcessBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #5c2599;
  background: rgba(89, 36, 155, 0.08);
  border: 1px solid rgba(89, 36, 155, 0.12);
`;

export const HistoryList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const HistoryItem = styled.li`
  position: relative;
  padding-left: 20px;
  padding-bottom: 16px;

  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 14px;
    bottom: 0;
    width: 2px;
    background: #ebe6f2;
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 4px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${props => props.theme.main.color};
    border: 2px solid #f6f1ff;
  }
`;

export const HistoryItemDate = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
`;

export const HistoryItemTitle = styled.div`
  margin-top: 4px;
  font-size: 0.92rem;
  color: #1f2937;
`;

export const HistoryItemMeta = styled.div`
  margin-top: 4px;
  font-size: 0.8rem;
  color: #6b7280;
`;

export const HistorySkeletonItem = styled.div`
  height: 64px;
  border-radius: 8px;
  margin-bottom: 12px;
  background: linear-gradient(90deg, #f3f0f8 25%, #ebe6f2 50%, #f3f0f8 75%);
  background-size: 200% 100%;
  animation: historySkeleton 1.2s ease-in-out infinite;

  @keyframes historySkeleton {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

export const ClickableTableRow = styled(Table.Row)`
  cursor: pointer;

  &:focus {
    outline: 2px solid rgba(89, 36, 155, 0.35);
    outline-offset: -2px;
  }
`;
